/**
 * INSERT + ATTRIB / ATTDEF serializer (§3.2 decision 7). Block references
 * are no longer exploded: the BLOCKS section carries the definition and the
 * ENTITIES section carries INSERT (66 attributes-follow) + one ATTRIB per
 * attribute value + SEQEND. Attribute text positions come from the block
 * definition's attdef positions transformed by the reference placement.
 */

import type { Entity } from '../../../../state/store';
import { useBlockStore, type BlockAttribute, type BlockDef } from '../../../../state/commandAuxStores';
import type { DxfWriteContext } from '../types';
import type { DxfStreamWriter } from '../DxfStreamWriter';
import { entityPrologue } from './common';

/** Transform a definition-local point into world space for one reference. */
function placeLocal(p: number[], pos: number[], scale: number, rotDeg: number): number[] {
    const rad = (rotDeg * Math.PI) / 180;
    const cos = Math.cos(rad), sin = Math.sin(rad);
    const x = (p[0] ?? 0) * scale, y = (p[1] ?? 1) * scale;
    return [
        (pos[0] ?? 0) + x * cos - y * sin,
        (pos[1] ?? 0) + x * sin + y * cos,
        (pos[2] ?? 0) + (p[2] ?? 0) * scale,
    ];
}

export function writeInsert(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    const name = String(d.blockName ?? '');
    const def = useBlockStore.getState().defs[name];
    if (!def) return;
    const pos = (Array.isArray(d.position) ? d.position : [0, 0, 0]) as number[];
    const scale = typeof d.scale === 'number' && d.scale > 0 ? d.scale : 1;
    const rot = typeof d.rotation === 'number' ? d.rotation : 0;
    const values = (d.attributeValues as Record<string, string>) ?? {};
    const hasAttribs = def.attributes.length > 0 && Object.keys(values).length > 0;

    entityPrologue(w, ctx, e, 'INSERT', owner);
    w.pair(100, 'AcDbBlockReference');
    w.pair(2, name);
    if (hasAttribs) w.pair(66, 1);
    w.pair(10, pos[0] ?? 0).pair(20, pos[1] ?? 0).pair(30, pos[2] ?? 0);
    if (scale !== 1) w.pair(41, scale).pair(42, scale).pair(43, scale);
    if (rot) w.pair(50, ((rot % 360) + 360) % 360);
    if (ctx.version.insertLockByte) w.pair(280, 0); // lock position off
    if (!hasAttribs) return;

    // ATTRIB children (in definition attribute order) + SEQEND.
    def.attributes.forEach((attr, i) => {
        writeAttrib(w, ctx, e, owner, def, attr, values[attr.tag] ?? attr.defaultValue, pos, scale, rot, i);
    });
    const seqHandle = ctx.handles.entityHandle(`dxf:seqend:${e.id}`);
    w.pair(0, 'SEQEND').pair(5, seqHandle).pair(330, owner);
    w.pair(100, 'AcDbEntity');
    w.pair(8, layerNameOfSafe(ctx, e));
}

function layerNameOfSafe(ctx: DxfWriteContext, e: Entity): string {
    const entry = ctx.layerEntries.get(e.layer);
    return entry?.layer.name ?? e.layer ?? '0';
}

function writeAttrib(
    w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string,
    def: BlockDef, attr: BlockAttribute, value: string,
    pos: number[], scale: number, rot: number, index: number,
): void {
    // Position: the definition's own attdef anchor when available.
    const src = def.entities.find((c) => c.type === 'attdef' && String((c.data as Record<string, unknown>).tag ?? '') === attr.tag);
    const local = (src ? ((src.data as Record<string, unknown>).position as number[]) : [0, 0, 0]) ?? [0, 0, 0];
    const world = placeLocal(local, pos, scale, rot);
    const height = src ? Number((src.data as Record<string, unknown>).height) || 0.25 : 0.25;

    const attribEntity: Entity = { ...e, id: `dxf:attr:${e.id}:${index}` };
    entityPrologue(w, ctx, attribEntity, 'ATTRIB', owner);
    w.pair(100, 'AcDbText');
    w.pair(10, world[0]).pair(20, world[1]).pair(30, world[2] ?? 0);
    w.pair(40, height);
    w.pair(1, String(value));
    if (rot) w.pair(50, ((rot % 360) + 360) % 360);
    w.pair(100, 'AcDbAttribute');
    w.pair(2, attr.tag);
    w.pair(7, 'STANDARD');
    w.pair(70, 0); // attribute flags (visible)
    w.pair(74, 0); // field length
    if (ctx.version.attribVersionByte) w.pair(280, 0); // attribute version byte (AC1024+)
}

/** ATTDEF for block definitions (and standalone attdef entities). */
export function writeAttdef(w: DxfStreamWriter, ctx: DxfWriteContext, e: Entity, owner: string): void {
    const d = e.data as Record<string, any>;
    const p = (Array.isArray(d.position) ? d.position : [0, 0, 0]) as number[];
    const height = typeof d.height === 'number' && d.height > 0 ? d.height : 0.25;
    entityPrologue(w, ctx, e, 'ATTDEF', owner);
    w.pair(100, 'AcDbText');
    w.pair(10, p[0] ?? 0).pair(20, p[1] ?? 0).pair(30, p[2] ?? 0);
    w.pair(40, height);
    w.pair(1, String(d.defaultValue ?? '')); // default value
    const rot = typeof d.rotation === 'number' ? d.rotation : 0;
    if (rot) w.pair(50, ((rot % 360) + 360) % 360);
    w.pair(100, 'AcDbAttributeDefinition');
    w.pair(3, String(d.prompt ?? '')); // prompt
    w.pair(2, String(d.tag ?? 'TAG')); // tag
    w.pair(70, 0); // flags: visible
    w.pair(74, 0); // field length
    if (ctx.version.attribVersionByte) w.pair(280, 0);
}
